import { FC, useMemo, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { FormSearch, PrimaryButton } from '..'
import { useAppSelector } from '../../store/hooks'
import { ServiceDTO } from '../../interfaces/Services/Services'
import { ShipmentGroup } from '../../interfaces/Import/ShipmentGroupInterface'

interface OriginOption {
  buCode: string
  buName?: string
  location?: { name?: string }
}

/**
 * Barra de acciones masivas del Review de carga masiva.
 *
 * Aparece al pie cuando hay al menos una guía seleccionada y aplica tienda origen
 * y/o servicio a todas de una vez. Sin esto, un archivo de aliado de 403 guías con
 * contrato multi-oficina exige abrir, elegir y cerrar el acordeón 403 veces —
 * `validateGroup` pide `originStoreCode` por guía y no hay otra forma de ponerlo.
 *
 * Se muestran SOLO los campos que el contrato deja elegir: con una sola tienda
 * origen el front la auto-asigna y con un solo servicio el Review también, así que
 * en esos casos el campo no aparece (mismo criterio que la fila y que las columnas
 * condicionales de la plantilla Excel).
 *
 * Los campos son FormSearch (combo con buscador), como el resto del Review, y con
 * el desplegable hacia arriba porque la barra vive pegada al borde inferior.
 */
export const ShipmentBulkUploadActionBar: FC<{
  count: number
  serviceOptions?: ServiceDTO[]
  onApply: (patch: Partial<ShipmentGroup>) => void
  onClear: () => void
}> = ({ count, serviceOptions = [], onApply, onClear }) => {
  // Igual que la fila: se lee del store en vez de prop-drillear por dos niveles.
  const user = useAppSelector((s) => s.user)
  const originBusinessUnits = (user.originBusinessUnits ?? []) as OriginOption[]

  const [origin, setOrigin] = useState<OriginOption | undefined>()
  const [service, setService] = useState<ServiceDTO | undefined>()
  // FormSearch es un combobox con buscador: el texto tecleado es estado propio y la
  // opción elegida es otro. Se mantienen los dos, igual que en la fila del acordeón.
  const [originSearch, setOriginSearch] = useState('')
  const [serviceSearch, setServiceSearch] = useState('')

  const originLabel = (b?: OriginOption) =>
    b ? `${b.buCode} - ${b.location?.name || b.buName || ''}` : ''
  const serviceLabel = (s?: ServiceDTO) => s?.serviceShortName ?? ''

  // La barra va sobre fondo oscuro (`text-white`), y los inputs de FormText/FormSearch
  // no fijan color propio: sin esto el texto sale blanco sobre el fondo blanco del
  // input y no se lee lo seleccionado.
  const fieldClassName = 'text-gray-900 bg-white'

  const showOrigin = originBusinessUnits.length > 1
  const showService = serviceOptions.length > 1

  // Nada que elegir → nada que aplicar. La barra no se renderiza en vez de
  // ofrecer un botón que no haría nada.
  const hasFields = showOrigin || showService
  const patch = useMemo<Partial<ShipmentGroup>>(() => {
    const p: Partial<ShipmentGroup> = {}
    if (showOrigin && origin) p.originStoreCode = origin.buCode
    if (showService && service) p.serviceId = service.serviceID
    return p
  }, [showOrigin, origin, showService, service])

  const canApply = Object.keys(patch).length > 0

  if (count === 0 || !hasFields) return null

  return (
    <div
      className="sticky bottom-0 z-20 mt-3 rounded-lg bg-gray-900 text-white px-4 py-3 shadow-lg flex flex-wrap items-end gap-3"
      role="region"
      aria-label="Acciones para las guías seleccionadas"
    >
      <span className="text-sm font-medium whitespace-nowrap self-center tabular-nums">
        {count} {count === 1 ? 'guía seleccionada' : 'guías seleccionadas'}
      </span>

      {showOrigin && (
        <FormSearch<OriginOption>
          id="bulk-action-origin"
          name="bulk-action-origin"
          label=""
          placeholder="Tienda origen"
          value={originSearch}
          options={originBusinessUnits}
          onChange={(e) => setOriginSearch(e.target.value)}
          onSelectOption={(opt) => {
            setOrigin(opt)
            setOriginSearch(originLabel(opt))
          }}
          onFilter={(opt, filter) => {
            // Contra el LABEL completo, igual que "Código de tienda" en la fila: al
            // reentrar el value ya es "MIA - Miami" y un filtro campo a campo lo
            // dejaría sin opciones.
            const q = (filter || '').toLowerCase().trim()
            return q === '' || originLabel(opt).toLowerCase().includes(q)
          }}
          RenderOption={({ option }) => (
            <div className="px-2 py-1 text-sm">{originLabel(option)}</div>
          )}
          className={`!h-9 ${fieldClassName}`}
          dropUp
          containerClassname="w-60"
        />
      )}

      {showService && (
        <FormSearch<ServiceDTO>
          id="bulk-action-service"
          name="bulk-action-service"
          label=""
          placeholder="Servicio"
          value={serviceSearch}
          options={serviceOptions}
          onChange={(e) => setServiceSearch(e.target.value)}
          onSelectOption={(opt) => {
            setService(opt)
            setServiceSearch(serviceLabel(opt))
          }}
          onFilter={(opt, filter) => {
            const q = (filter || '').toLowerCase().trim()
            return q === '' || serviceLabel(opt).toLowerCase().includes(q)
          }}
          RenderOption={({ option }) => (
            <div className="px-2 py-1 text-sm">{serviceLabel(option)}</div>
          )}
          className={`!h-9 ${fieldClassName}`}
          dropUp
          containerClassname="w-52"
        />
      )}

      <PrimaryButton
        className="h-9 text-sm px-4 ml-auto"
        disabled={!canApply}
        onClick={() => {
          onApply(patch)
          // Se limpian los campos, no la selección: es normal aplicar tienda origen
          // y después servicio sobre el mismo lote.
          setOrigin(undefined)
          setService(undefined)
          setOriginSearch('')
          setServiceSearch('')
        }}
      >
        Aplicar a {count}
      </PrimaryButton>

      <button
        type="button"
        onClick={onClear}
        className="h-9 px-2 text-gray-300 hover:text-white self-center"
        aria-label="Quitar la selección"
        title="Quitar la selección"
      >
        <XMarkIcon className="w-5 h-5" aria-hidden="true" />
      </button>
    </div>
  )
}
